import isEmpty from 'lodash.isempty';
import i18next from '../../i18n';

export const rejectErrorCodeHelper = (error) => {
  if (error?.response?.status === 429) {
    return i18next.t('apiErrors.TooManyRequests');
  }
  if (error?.response?.data?.ErrorMessage) {
    return error?.response?.data?.ErrorMessage;
  }

  return i18next.t('apiErrors.SomethingWentWrong');
};

export function formatErrorMessage(error) {
  const errorCode = error?.response?.data?.Errors[0]?.Code;

  if (errorCode === 'SocureKYCError') {
    const errorMessage = error?.response?.data?.Errors[0]?.Message;
    return errorMessage.slice(30);
  }

  return i18next.t(`apiErrors.${errorCode}`);
}

export function handleOnCompleteError(error) {
  const errorResponse = error?.response;
  const errorData = errorResponse?.data;
  const errorsList = errorData?.Errors;
  const errorCode = errorsList[0].Code;
  return i18next.t(`apiErrors.${errorCode}`);
}

export function handleBankAccountError({ error, setErrorMessages }) {
  const errorResponse = error.response;
  const errorData = errorResponse.data;
  const list = errorData.Errors;
  const errorMessage = (error) => {
    switch (error.Context) {
      case 'CanAddBankAccount':
        if (error.Code === 'WizardValidation_InvalidApplicationStepType') {
          setErrorMessages([
            i18next.t('fields.errors.validation.invalidApplicationStep'),
          ]);
        } else {
          setErrorMessages(
            list
              .filter(
                (errorObject) => errorObject.Context === 'CanAddBankAccount',
              )
              .map((e) => e.Message),
          );
        }
        break;
      default:
        break;
    }
  };

  if (!isEmpty(list)) {
    list.map((error) => {
      errorMessage(error);

      return null;
    });
  }

  return null;
}

export function handleErrorBasedOnCode(error, onError) {
  const errorData = error?.response?.data;
  const list = errorData.Errors;

  const errorMessage = (error) => {
    switch (error.Code) {
      case 'StreetIsNullOrEmpty':
      case 'StreetIsInvalidLength':
        onError('Street', {
          type: 'manual',
          message: i18next.t(`apiErrors.${error.Code}`),
        });
        break;
      case 'CityIsNullOrEmpty':
      case 'CityIsInvalidLength':
        onError('City', {
          type: 'manual',
          message: i18next.t(`apiErrors.${error.Code}`),
        });
        break;
      case 'CountryIso3CodeIsNullOrEmpty':
      case 'CountryIso3CodeIsInvalidLength':
      case 'CountryIso3CodeIsInvalid':
        onError('CountryIso3CodeIsInvalid', {
          type: 'manual',
          message: i18next.t(`apiErrors.${error.Code}`),
        });
        break;
      case 'PostalCodeIsNullOrEmpty':
      case 'PostalCodeIsInvalidLength':
      case 'PostalCodeNotValid':
      case 'PostalCodeIsInvalid':
        onError('PostalCode', {
          type: 'manual',
          message: i18next.t(`apiErrors.${error.Code}`),
        });
        break;
      case 'StateIsNullOrEmpty':
      case 'StateIsInvalidLength':
      case 'GiftStateNotSupported':
        onError('State', {
          type: 'manual',
          message: i18next.t(`apiErrors.${error.Code}`),
        });
        break;

      default:
        break;
    }
  };

  if (!isEmpty(list)) {
    list.map((error) => {
      errorMessage(error);

      return null;
    });
  }

  return null;
}

export function formatErrorMessageBaseOnCodeIDVerification(error) {
  const errorCode = error?.response?.data?.Errors[0]?.Code;
  let errorMessage = error?.response?.data?.Errors[0]?.Message;
  if (errorMessage) {
    errorMessage = errorMessage.replace(/^BadRequestValidation\s*-\s*/, '');
  }
  let fieldName = '';

  switch (errorCode) {
    case 'IdentificationTypeIsInvalid':
      fieldName = 'IDType';
      break;

    case 'IDNumberIsInvalid':
    case 'IDNumberIsNull':
      fieldName = 'IDNumber';
      break;

    case 'IDIssueDateIsInvalid':
    case 'IDIssueDateIsNull':
      fieldName = 'IssueDate';
      break;

    case 'IDExpirationDateIsInvalid':
    case 'IDExpirationDateIsNull':
      fieldName = 'ExpiryDate';
      break;

    case 'IssuedByCountryIsInvalid':
    case 'IssuedByCountryIsNull':
      fieldName = 'IssuedBy';
      break;

    case 'IssuedByStateIsInvalid':
    case 'IssuedByStateIsNull':
      fieldName = 'State';
      break;
    case 'UnnecessaryDataProvided':
      fieldName = 'general';
      break;

    default:
      fieldName = '';
  }

  return {
    fieldName,
    errorMessage:
      fieldName === 'general'
        ? errorMessage
        : i18next.t(`apiErrors.${errorCode}`),
  };
}

export function formatErrorMessageBaseOnCodeFinancialProfile(error) {
  const errorCode = error?.response?.data?.Errors[0]?.Code;
  let fieldName = '';

  switch (errorCode) {
    case 'AccountPurposeTypeIsNull':
    case 'AccountPurposeTypeIsInvalid':
      fieldName = 'AccountPurpose';
      break;

    case 'InitialFundingSourceTypesIsNull':
    case 'InitialFundingSourceTypesNoneSelected':
    case 'InitialFundingSourceTypesIsInvalid':
      fieldName = 'InitialFundingSourceTypes';
      break;

    case 'InitialFundingSourceTypesOtherDetailsMissing':
    case 'InitialFundingSourceTypesOtherDetailsNotNeeded':
      fieldName = 'InitialFundingOtherDetails';
      break;

    case 'OngoingFundingSourceTypesIsNull':
    case 'OngoingFundingSourceTypesNoneSelected':
    case 'OngoingFundingSourceTypesIsInvalid':
      fieldName = 'OngoingFundingSourceTypes';
      break;

    case 'OngoingFundingSourceTypesOtherDetailsMissing':
    case 'OngoingFundingSourceTypesOtherDetailsNotNeeded':
      fieldName = 'OngoingFundingOtherDetails';
      break;

    default:
      fieldName = '';
  }

  return {
    fieldName,
    errorMessage: i18next.t(`apiErrors.${errorCode}`),
  };
}

export function formatErrorMessageBaseOnCodeEmploymentStatus(error) {
  const errorCode = error?.response?.data?.Errors[0]?.Code;
  let fieldName = '';
  let customMessage = '';

  switch (errorCode) {
    case 'The EmploymentStatus field is required.':
      fieldName = 'EmploymentStatus';
      customMessage = i18next.t('apiErrors.EmploymentStatusTypeIsNull');
      break;

    case 'EmploymentStatusTypeIsNull':
      fieldName = 'EmploymentStatus';
      break;

    case 'OccupationCategoryIsNull':
    case 'OccupationCategoryIsInvalid':
      fieldName = 'OccupationCategory';
      break;

    case 'OccupationIsNull':
    case 'OccupationIsInvalid':
      fieldName = 'Occupation';
      break;

    case 'PriorOccupationIsNull':
    case 'PriorOccupationIsInvalid':
      fieldName = 'Occupation';
      break;

    case 'PriorOccupationCategoryIsNull':
    case 'PriorOccupationCategoryIsInvalid':
      fieldName = 'OccupationCategory';
      break;

    case 'EmployerNameIsNull':
      fieldName = 'EmployerName';
      break;

    case 'IncomeSourceTypeIsNull':
    case 'IncomeSourceTypeIsInvalid':
      fieldName = 'SourceOfIncome';
      break;

    case 'EmployerAddressLineIsNull':
    case 'EmployerAddressLineIsInvalidLength':
      fieldName = 'EmployerAddressLine';
      break;

    case 'EmployerCityIsNull':
    case 'EmployerCityIsInvalidLength':
      fieldName = 'EmployerCity';
      break;

    case 'EmployerStateIsNull':
    case 'EmployerStateIsInvalidLength':
    case 'EmployerStateIsInvalid':
      fieldName = 'EmployerState';
      break;

    case 'EmployerZipCodeIsNull':
    case 'EmployerZipCodeIsInvalidLength':
    case 'EmployerZipCodeIsNotValid':
      fieldName = 'EmployerZipCode';
      break;

    default:
      fieldName = '';
  }

  return {
    fieldName,
    errorMessage: customMessage || i18next.t(`apiErrors.${errorCode}`),
  };
}
